import {
  AiSession,
  LearningTrackTopic,
  LearningTrackTopicComponent,
  SessionContextMode,
} from "../types/ai-session.entity";
import {
  getAISessionContext,
  getTopicContextDisplayLabels,
  getTopicContextItems,
  getTopicContextTopicIds,
} from "./sessionContext";
import { getUniqueContextDisplayValues, normalizeContextText } from "./sessionContextItems";

type AISessionContextSource = Partial<Pick<AiSession, "context">> & {
  context?: unknown;
  topics?: unknown;
};

type TopicScenarioLookup = Map<string, { situation?: unknown } | undefined>;

type AISessionContextDisplayOptions = {
  scenarioById?: TopicScenarioLookup;
};

const getSessionTopicContextLabels = (
  session: AISessionContextSource,
  options?: AISessionContextDisplayOptions
): string[] => {
  const context = getAISessionContext(session);

  if (context.sessionMode !== "TOPIC") {
    return [];
  }

  return getTopicContextDisplayLabels(getTopicContextItems(context.items), options?.scenarioById);
};

const getLearningTrackTitles = (topics: LearningTrackTopic[]): string[] =>
  getUniqueContextDisplayValues(
    topics.map((topic: LearningTrackTopic, index: number) => {
      const title = normalizeContextText(topic.topicTitle);
      return title ?? `Learning Track Topic ${index + 1}`;
    })
  );

const formatDisplayLabels = (labels: string[]): string => {
  if (labels.length === 0) return "None";
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
};

export const getAISessionContextMode = (
  session: AISessionContextSource
): SessionContextMode => getAISessionContext(session).sessionMode;

export const getAISessionContextItemCount = (
  session: AISessionContextSource
): number => getAISessionContext(session).items.length;

export const getAISessionContextDisplayLabels = (
  session: AISessionContextSource,
  options?: AISessionContextDisplayOptions
): string[] => {
  const context = getAISessionContext(session);

  if (context.sessionMode === "TOPIC") {
    return getSessionTopicContextLabels(session, options);
  }

  if (context.sessionMode === "LEARNING_TRACK") {
    return getLearningTrackTitles(context.items);
  }

  return [];
};

export const getAISessionContextSummary = (
  session: AISessionContextSource,
  options?: AISessionContextDisplayOptions
): string => {
  const context = getAISessionContext(session);
  const itemCount = getAISessionContextItemCount(session);

  if (itemCount === 0) {
    return "None";
  }

  if (context.sessionMode === "TOPIC") {
    return formatDisplayLabels(getAISessionContextDisplayLabels(session, options));
  }

  if (context.sessionMode === "LEARNING_TRACK") {
    return formatDisplayLabels(getAISessionContextDisplayLabels(session, options));
  }

  return itemCount === 1 ? "1 word" : `${itemCount} words`;
};

export const getAISessionLanguage = (
  session: Pick<AiSession, "langCode">
): string | null => normalizeContextText(session.langCode);

export const getAISessionTopicDisplayLabels = (
  session: AISessionContextSource
): string[] => {
  const context = getAISessionContext(session);
  return context.sessionMode === "TOPIC"
    ? getTopicContextTopicIds(getTopicContextItems(context.items))
    : [];
};

export const getAISessionTopicSlugs = getAISessionTopicDisplayLabels;

export const getAISessionLearningTrackTopics = (
  session: AISessionContextSource
): LearningTrackTopic[] => {
  const context = getAISessionContext(session);
  return context.sessionMode === "LEARNING_TRACK" ? context.items : [];
};

export type { LearningTrackTopicComponent };
